import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { landsAPI, documentsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Map,
  MapPin,
  Trash2,
  Edit,
  Loader2,
  Search,
  FileText,
  Upload,
  CheckCircle2,
  Clock,
  Eye,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Map click handler component
const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
};

const LandsPage = () => {
  const [lands, setLands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLand, setEditingLand] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    address: '',
    city: 'Gaborone',
    plot_number: '',
    size_sqm: '',
    land_type: 'residential',
    zoning: '',
    coordinates: null,
  });
  
  // Document upload state
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [selectedLandForDoc, setSelectedLandForDoc] = useState(null);
  const [landDocuments, setLandDocuments] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docForm, setDocForm] = useState({
    document_type: 'title_deed',
    file_name: '',
    file_url: '',
  });

  useEffect(() => {
    loadLands();
  }, []);

  const loadLands = async () => {
    try {
      const response = await landsAPI.getAll();
      setLands(response.data);
    } catch (error) {
      console.error('Failed to load lands:', error);
      toast.error('Failed to load lands');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleLocationSelect = (coords) => {
    setFormData({ ...formData, coordinates: coords });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      address: '',
      city: 'Gaborone',
      plot_number: '',
      size_sqm: '',
      land_type: 'residential',
      zoning: '',
      coordinates: null,
    });
    setEditingLand(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...formData,
        size_sqm: parseFloat(formData.size_sqm),
      };

      if (editingLand) {
        await landsAPI.update(editingLand.land_id, data);
        toast.success('Land updated successfully');
      } else {
        await landsAPI.create(data);
        toast.success('Land registered successfully');
      }

      setDialogOpen(false);
      resetForm();
      loadLands();
    } catch (error) {
      const message = error.response?.data?.detail || 'Operation failed';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (land) => {
    setEditingLand(land);
    setFormData({
      title: land.title,
      description: land.description || '',
      address: land.address,
      city: land.city,
      plot_number: land.plot_number || '',
      size_sqm: land.size_sqm.toString(),
      land_type: land.land_type,
      zoning: land.zoning || '',
      coordinates: land.coordinates,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (landId) => {
    if (!window.confirm('Are you sure you want to delete this land?')) return;

    try {
      await landsAPI.delete(landId);
      toast.success('Land deleted successfully');
      loadLands();
    } catch (error) {
      toast.error('Failed to delete land');
    }
  };

  const filteredLands = lands.filter(
    (land) =>
      land.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      land.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      land.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Document management functions
  const openDocModal = async (land) => {
    setSelectedLandForDoc(land);
    setDocModalOpen(true);
    setDocLoading(true);
    try {
      const response = await documentsAPI.getAll(land.land_id);
      setLandDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setDocLoading(false);
    }
  };

  const handleDocUpload = async (e) => {
    e.preventDefault();
    if (!docForm.file_name) {
      toast.error('Please enter a document name');
      return;
    }
    
    setUploadingDoc(true);
    try {
      // For now, we'll use a mock URL. In production, you'd upload to cloud storage first
      const docData = {
        ...docForm,
        file_url: docForm.file_url || `https://storage.example.com/${Date.now()}_${docForm.file_name}`,
        file_size: 0,
      };
      
      await documentsAPI.upload(selectedLandForDoc.land_id, docData);
      toast.success('Document uploaded successfully');
      
      // Reload documents
      const response = await documentsAPI.getAll(selectedLandForDoc.land_id);
      setLandDocuments(response.data.documents || []);
      setDocForm({ document_type: 'title_deed', file_name: '', file_url: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload document';
      toast.error(message);
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await documentsAPI.delete(selectedLandForDoc.land_id, documentId);
      toast.success('Document deleted');
      setLandDocuments(landDocuments.filter(d => d.document_id !== documentId));
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getDocTypeLabel = (type) => {
    const labels = {
      title_deed: 'Title Deed',
      survey: 'Survey Report',
      zoning_approval: 'Zoning Approval',
      valuation: 'Valuation Report',
      other: 'Other Document',
    };
    return labels[type] || type;
  };

  const cities = ['Gaborone', 'Francistown', 'Maun', 'Kasane', 'Palapye', 'Serowe', 'Selebi-Phikwe', 'Lobatse'];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="lands-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">My Lands</h1>
            <p className="text-muted-foreground mt-1">
              Manage your registered land parcels
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-land-btn">
                <Plus className="w-4 h-4 mr-2" />
                Register Land
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingLand ? 'Edit Land' : 'Register New Land'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Land Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="e.g., My Plot in Block 9"
                      required
                      data-testid="land-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="plot_number">Plot Number</Label>
                    <Input
                      id="plot_number"
                      name="plot_number"
                      value={formData.plot_number}
                      onChange={handleInputChange}
                      placeholder="e.g., 1234"
                      data-testid="plot-number-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the land..."
                    rows={3}
                    data-testid="land-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Street address"
                      required
                      data-testid="land-address-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Select 
                      value={formData.city} 
                      onValueChange={(v) => handleSelectChange('city', v)}
                    >
                      <SelectTrigger data-testid="city-select">
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map((city) => (
                          <SelectItem key={city} value={city}>{city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="size_sqm">Size (sqm) *</Label>
                    <Input
                      id="size_sqm"
                      name="size_sqm"
                      type="number"
                      value={formData.size_sqm}
                      onChange={handleInputChange}
                      placeholder="e.g., 1200"
                      required
                      min="1"
                      data-testid="land-size-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="land_type">Land Type *</Label>
                    <Select 
                      value={formData.land_type} 
                      onValueChange={(v) => handleSelectChange('land_type', v)}
                    >
                      <SelectTrigger data-testid="land-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="industrial">Industrial</SelectItem>
                        <SelectItem value="agricultural">Agricultural</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zoning">Zoning Status</Label>
                  <Select 
                    value={formData.zoning || ''} 
                    onValueChange={(v) => handleSelectChange('zoning', v)}
                  >
                    <SelectTrigger data-testid="zoning-select">
                      <SelectValue placeholder="Select zoning status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="not_applied">Not Applied</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Map for location selection */}
                <div className="space-y-2">
                  <Label>Location (Click on map to set)</Label>
                  <div className="h-[250px] rounded-sm border border-border overflow-hidden">
                    <MapContainer
                      center={formData.coordinates || [-24.6282, 25.9231]}
                      zoom={13}
                      className="h-full w-full"
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapClickHandler onLocationSelect={handleLocationSelect} />
                      {formData.coordinates && (
                        <Marker position={[formData.coordinates.lat, formData.coordinates.lng]} />
                      )}
                    </MapContainer>
                  </div>
                  {formData.coordinates && (
                    <p className="text-xs text-muted-foreground">
                      Selected: {formData.coordinates.lat.toFixed(6)}, {formData.coordinates.lng.toFixed(6)}
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="submit-land-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      editingLand ? 'Update Land' : 'Register Land'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search lands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-lands-input"
          />
        </div>

        {/* Lands Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredLands.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLands.map((land) => (
              <Card 
                key={land.land_id} 
                className="border border-border hover:border-secondary/50 transition-colors"
                data-testid={`land-card-${land.land_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-[#0F2640] rounded-sm flex items-center justify-center">
                      <Map className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xs font-medium uppercase bg-muted px-2 py-1 rounded">
                      {land.land_type}
                    </span>
                  </div>

                  <h3 className="font-heading font-bold text-lg mb-2">{land.title}</h3>
                  
                  <div className="space-y-2 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{land.address}, {land.city}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span>{land.size_sqm.toLocaleString()} sqm</span>
                      {land.plot_number && <span>Plot #{land.plot_number}</span>}
                    </div>
                  </div>

                  {land.coordinates && (
                    <div className="h-32 rounded-sm overflow-hidden mb-4">
                      <MapContainer
                        center={[land.coordinates.lat, land.coordinates.lng]}
                        zoom={15}
                        className="h-full w-full"
                        zoomControl={false}
                        dragging={false}
                        scrollWheelZoom={false}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Marker position={[land.coordinates.lat, land.coordinates.lng]} />
                      </MapContainer>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openDocModal(land)}
                      data-testid={`docs-land-${land.land_id}`}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Docs
                      {land.documents?.length > 0 && (
                        <span className="ml-1 text-xs bg-muted px-1.5 py-0.5 rounded">
                          {land.documents.length}
                        </span>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(land)}
                      data-testid={`edit-land-${land.land_id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(land.land_id)}
                      data-testid={`delete-land-${land.land_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border border-border">
            <CardContent className="p-12 text-center">
              <Map className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg mb-2">No lands registered</h3>
              <p className="text-muted-foreground mb-4">
                Start by registering your first land parcel
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Register Land
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Document Upload Modal */}
        <Dialog open={docModalOpen} onOpenChange={setDocModalOpen}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents - {selectedLandForDoc?.title}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Existing Documents */}
              <div>
                <h4 className="font-medium mb-3">Uploaded Documents</h4>
                {docLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : landDocuments.length > 0 ? (
                  <div className="space-y-2">
                    {landDocuments.map((doc) => (
                      <div 
                        key={doc.document_id}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-sm"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium text-sm">{doc.file_name}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{getDocTypeLabel(doc.document_type)}</span>
                              {doc.verified ? (
                                <span className="flex items-center gap-1 text-emerald-600">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Verified
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-amber-600">
                                  <Clock className="w-3 h-3" />
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {doc.file_url && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => window.open(doc.file_url, '_blank')}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDocDelete(doc.document_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No documents uploaded yet
                  </p>
                )}
              </div>

              {/* Upload Form */}
              <div className="pt-4 border-t">
                <h4 className="font-medium mb-3">Upload New Document</h4>
                <form onSubmit={handleDocUpload} className="space-y-3">
                  <div className="space-y-2">
                    <Label>Document Type</Label>
                    <Select 
                      value={docForm.document_type} 
                      onValueChange={(v) => setDocForm({...docForm, document_type: v})}
                    >
                      <SelectTrigger data-testid="doc-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="title_deed">Title Deed</SelectItem>
                        <SelectItem value="survey">Survey Report</SelectItem>
                        <SelectItem value="zoning_approval">Zoning Approval</SelectItem>
                        <SelectItem value="valuation">Valuation Report</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Document Name *</Label>
                    <Input
                      value={docForm.file_name}
                      onChange={(e) => setDocForm({...docForm, file_name: e.target.value})}
                      placeholder="e.g., Title_Deed_Plot_1234.pdf"
                      data-testid="doc-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Document URL (Optional)</Label>
                    <Input
                      value={docForm.file_url}
                      onChange={(e) => setDocForm({...docForm, file_url: e.target.value})}
                      placeholder="https://..."
                      data-testid="doc-url-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter a URL to an existing document, or leave empty for placeholder
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#10B981] hover:bg-[#0ea572]"
                    disabled={uploadingDoc}
                    data-testid="upload-doc-btn"
                  >
                    {uploadingDoc ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Document
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LandsPage;
